import type { EventFilters, MapBounds } from "../types/eventFilters";
import { EVENT_TYPE_ITEMS } from "../components/filters/constants";

const EVENT_TYPE_IDS = new Set(EVENT_TYPE_ITEMS.map((i) => i.id));

function parseEventTypes(params: URLSearchParams): string[] {
    const raw = params.get("event_type")?.trim();
    if (!raw) return [];
    const out: string[] = [];
    for (const part of raw.split(",")) {
        const p = part.trim().toUpperCase();
        if (p && EVENT_TYPE_IDS.has(p)) out.push(p);
    }
    return [...new Set(out)].sort();
}

const COORD_PRECISION = 6;

function roundCoord(n: number): number {
    const f = 10 ** COORD_PRECISION;
    return Math.round(n * f) / f;
}

function parseBounds(params: URLSearchParams): MapBounds | null {
    const keys = ["min_lng", "min_lat", "max_lng", "max_lat"] as const;
    const raw: Record<string, string> = {};
    for (const k of keys) {
        const v = params.get(k);
        if (v == null || v === "") return null;
        raw[k] = v;
    }
    const minLng = roundCoord(Number(raw.min_lng));
    const minLat = roundCoord(Number(raw.min_lat));
    const maxLng = roundCoord(Number(raw.max_lng));
    const maxLat = roundCoord(Number(raw.max_lat));
    if (
        !Number.isFinite(minLng) ||
        !Number.isFinite(minLat) ||
        !Number.isFinite(maxLng) ||
        !Number.isFinite(maxLat)
    ) {
        return null;
    }
    return { minLng, minLat, maxLng, maxLat };
}

function parseWeekdays(params: URLSearchParams): number[] {
    const raw = params.get("weekdays");
    if (!raw?.trim()) return [];
    const out: number[] = [];
    for (const part of raw.split(",")) {
        const n = Number(part.trim());
        if (Number.isInteger(n) && n >= 1 && n <= 7) out.push(n);
    }
    return [...new Set(out)].sort((a, b) => a - b);
}

/** Parse URL search params into filter state. */
export function parseEventFiltersFromSearchParams(
    params: URLSearchParams
): EventFilters {
    const fromDate = params.get("from")?.trim() || null;
    const toDate = params.get("to")?.trim() || null;
    return {
        fromDate: fromDate || null,
        toDate: toDate || null,
        weekdays: parseWeekdays(params),
        eventTypes: parseEventTypes(params),
        bounds: parseBounds(params),
    };
}

/** Serialize filters to query string (stable key order, omits empties). */
export function serializeEventFiltersToSearchParams(
    filters: EventFilters
): URLSearchParams {
    const sp = new URLSearchParams();
    if (filters.fromDate) sp.set("from", filters.fromDate);
    if (filters.toDate) sp.set("to", filters.toDate);
    if (filters.weekdays.length)
        sp.set("weekdays", filters.weekdays.join(","));
    if (filters.eventTypes.length)
        sp.set("event_type", filters.eventTypes.join(","));
    if (filters.bounds) {
        const b = filters.bounds;
        sp.set("min_lng", String(roundCoord(b.minLng)));
        sp.set("min_lat", String(roundCoord(b.minLat)));
        sp.set("max_lng", String(roundCoord(b.maxLng)));
        sp.set("max_lat", String(roundCoord(b.maxLat)));
    }
    return sp;
}

export function filtersEqual(a: EventFilters, b: EventFilters): boolean {
    if (a.fromDate !== b.fromDate || a.toDate !== b.toDate) return false;
    if (a.eventTypes.length !== b.eventTypes.length) return false;
    if (!a.eventTypes.every((t, i) => t === b.eventTypes[i])) return false;
    if (a.weekdays.length !== b.weekdays.length) return false;
    if (!a.weekdays.every((d, i) => d === b.weekdays[i])) return false;
    if (!!a.bounds !== !!b.bounds) return false;
    if (!a.bounds && !b.bounds) return true;
    if (!a.bounds || !b.bounds) return false;
    return (
        roundCoord(a.bounds.minLng) === roundCoord(b.bounds.minLng) &&
        roundCoord(a.bounds.minLat) === roundCoord(b.bounds.minLat) &&
        roundCoord(a.bounds.maxLng) === roundCoord(b.bounds.maxLng) &&
        roundCoord(a.bounds.maxLat) === roundCoord(b.bounds.maxLat)
    );
}

export function mergeEventFilters(
    base: EventFilters,
    patch: Partial<EventFilters>
): EventFilters {
    return {
        fromDate: patch.fromDate !== undefined ? patch.fromDate : base.fromDate,
        toDate: patch.toDate !== undefined ? patch.toDate : base.toDate,
        weekdays: patch.weekdays !== undefined ? patch.weekdays : base.weekdays,
        eventTypes:
            patch.eventTypes !== undefined ? patch.eventTypes : base.eventTypes,
        bounds: patch.bounds !== undefined ? patch.bounds : base.bounds,
    };
}
